import { ConnectButton } from "app/components/connect-button/connect-button";
import Button from "app/components/button/button";
import {
  setAuthInProgress,
  useAuth,
  useAuthInProgress,
  useLogin,
} from "app/hooks";
import { FC, ReactNode, useEffect, useState } from "react";

import { useAccount } from "wagmi";

import { useUserEventMutation } from "store/api/new.api";
import styles from "./header-connect.module.scss";

type Props = {
  onConnect?: () => void;
  title?: string;
  children?: ReactNode;
  requireSolana?: boolean;
};

const HeaderConnect: FC<Props> = ({
  onConnect,
  title,
  children,
  requireSolana = false,
}) => {
  const [trackEvent] = useUserEventMutation();
  const { address, isConnected } = useAccount();
  const isAuth = useAuth();
  const authInProgress = useAuthInProgress();
  const [eventSent, setEventSent] = useState<boolean>(false);
  const {
    loginMutation,
    solanaWarning,
    setSolanaWarning,
    isLoading: isAuthRetryLoading,
  } = useLogin();
  const [showSolanaAlert, setShowSolanaAlert] = useState(false);

  // Send event after successful authentication
  useEffect(() => {
    if (isConnected && address && isAuth && !eventSent) {
      setEventSent(true);
      trackEvent({ event: "Login", ethAddress: address })
        .unwrap()
        .then(() => {
          if (onConnect) {
            onConnect();
          }
        })
        .catch((error) => {
          console.error("Event tracking failed:", error);
          // Don't reset eventSent - not critical if event tracking fails
        });
    } else if (!isConnected || !address || !isAuth) {
      // Reset event sent flag when user disconnects or logs out
      setEventSent(false);
    }
  }, [isConnected, address, isAuth, trackEvent, onConnect, eventSent]);

  const sendConnectEvent = () => {
    if (!eventSent && typeof window !== "undefined" && window.twq) {
      window.twq("event", "tw-ona64", {});
      setEventSent(true);
    } else {
      console.error("Twitter Pixel is not initialized or event already sent.");
    }
  };

  const handleAuthRetry = async () => {
    if (!isConnected || !address || isAuth || authInProgress) {
      return;
    }

    setAuthInProgress(true);

    try {
      const result = await loginMutation(address, requireSolana);
      if (!result.success && solanaWarning) {
        setShowSolanaAlert(true);
        setTimeout(() => setShowSolanaAlert(false), 5000); // Auto-hide after 5 seconds
        return;
      }

      if (!eventSent) {
        sendConnectEvent();
      }
    } finally {
      setAuthInProgress(false);
    }
  };

  return (
    <>
      {!isAuth ? (
        <>
          <div className={title === "login" ? styles.landing : styles.btn}>
            {isConnected && address ? (
              <Button
                onClick={handleAuthRetry}
                type="button"
                disabled={authInProgress || isAuthRetryLoading}
              >
                {authInProgress || isAuthRetryLoading
                  ? "Signing..."
                  : "Sign Wallet"}
              </Button>
            ) : (
              <ConnectButton />
            )}
          </div>
          {showSolanaAlert && solanaWarning && (
            <div className={styles.solanaAlert}>
              <div className={styles.alertContent}>
                <span className={styles.alertIcon}>⚠️</span>
                <span className={styles.alertText}>{solanaWarning}</span>
                <button
                  className={styles.alertClose}
                  onClick={() => {
                    setShowSolanaAlert(false);
                    setSolanaWarning(null);
                  }}
                >
                  ×
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        <>{children}</>
      )}
    </>
  );
};

export default HeaderConnect;
